import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Tooltip from '@mui/material/Tooltip';
import TableBody from '@mui/material/TableBody';
import IconButton from '@mui/material/IconButton';
import { Tab, Tabs, Button, Skeleton, TableRow, TableCell, Typography } from '@mui/material';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import {
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableRowCustom,
  TableHeadCustom,
  TableSelectedAction,
  TablePaginationCustom,
} from 'src/components/table';

import { Label } from '../../../label';
import { ConfirmDialog } from '../../../custom-dialog';
import { TableToolbar } from '../toolbar/table-toolbar';

import type { TableHeadCellProps } from '../head/table-head-custom';

// ----------------------------------------------------------------------

type TableCustomViewProps = {
  // Original props that View needs
  columns: TableHeadCellProps[];
  tableToolbar?: boolean;
  actionButton?: string;
  actionButtonHandler?: () => void;
  actionButtonIcon?: string;
  secondaryActionButton?: string;
  secondaryActionButtonHandler?: () => void;
  secondaryActionButtonIcon?: string;
  editHref: (id: string) => string;
  contactDetailHref?: (id: string) => string;
  accountDetailHref?: (id: string) => string;
  userDetailHref?: (id: string) => string;
  teamsDetailHref?: (id: string) => string;
  handleEditContact?: (id: string) => string | Promise<void>;
  tenantUserDetailsHref?: (tenantId: string, userId: string) => string;
  tenantInvoiceDetailsHref?: (tenantId: string, userId: string) => string;
  isToolbarVisible?: boolean;
  isCheckboxVisible?: boolean;
  rowHeight?: boolean;
  isSelectRowPremission?: boolean;
  isActionButton?: Array<'import' | 'export'>;
  actionhref?: string;
  isShowCreateBtn?: boolean;
  isSelectRecordDialog?: boolean;
  setRecordInputHandler?: (row: any) => void;
  isSearchFullWidth?: boolean;
  handleDeleteRecords?: (ids: string[]) => void;
  handleMergeRecords?: (ids: string[], clearSelection: () => void) => void;
  /** Replaces the built-in delete confirm for bulk delete (the page shows its own dialog) */
  handleBulkDeleteClick?: (ids: string[], clearSelection: () => void) => void;
  handleIVRConnection?: (id: string) => void;
  handleCustomiseDID?: (id: string) => void;
  handleToggleStatus?: (row: any) => void;
  handleQuickEdit?: (row: any) => void;
  loader?: boolean;
  handleResendMail?: (id: string) => string;
  handlePreview?: (id: any) => void;
  playCallRecording?: (id: string) => void;
  isCampaignTamplate?: boolean;
  isSelectAudience?: boolean;
  onFileSelect?: (fileData: any) => void;
  onExportContact?: () => void;
  currentModule?: string;
  currentlyPlayingId?: string | null;
  advanceFilterFields?: any;
  isAdvanceFilter?: boolean;
  refreshPage?: () => void;
  refreshing?: boolean;
  tabFilters?: boolean;
  customTabs?: any;
  handleLoginAsUser?: (row: any) => void;
  handleOpenConversation?: (row: any) => void;
  isDisableSync?: boolean;
  userRole?: string;
  isSupportReport?: boolean;
  isSearchInput?: boolean;
  isPrivacyPolicyUrls?: {
    message: string;
    links: { label: string; url: string }[];
  } | null;
  displayPagination?: boolean;
  isTenantInvoice?: boolean;
  isSuperAdmin?: boolean;
  handleMarkAsRead?: (id: string) => void;
  currentTenantId?: string;
  handleAddFriend?: (row: any) => void;
  handlePayNowButton?: (id: any) => void;
  /** When set, invoice View / Pay Now are disabled and this text is shown as the reason. */
  invoiceLockedMessage?: string;
  manageColumnsModule?: string;
  handleApproveLearning?: (id: string) => void | Promise<void>;
  handleRejectLearning?: (id: string) => void | Promise<void>;
  isApprovingLearning?: boolean;
  isRejectingLearning?: boolean;
  handleResolveKnowledgeGap?: (id: string) => void | Promise<void>;
  isResolvingKnowledgeGap?: boolean;
  isRowInteractionLocked?: (row: any) => boolean;
  rowInteractionLockedTooltip?: string;
  getRowInteractionLockedTooltip?: (row: any) => string;
  customFields?: any[];
  disableSelectAll?: boolean;
  handleRefresh?: (id: string) => void;

  // Flattened props from logic hook
  t: any;
  moduleName: string;
  isCreate: boolean;
  isView: boolean;
  isImport: boolean;
  isExport: boolean;
  isEdit: boolean;
  isDelete: boolean;
  table: any;
  filters: any;
  visibleColumns: string[];
  setVisibleColumns: any;
  onResetPage: () => void;
  selectedfilters: any;
  dateTimeFiltre: any;
  handleRemoveAllFilter: () => void;
  resetPagination: () => void;
  order: any;
  orderBy: any;
  headCells: TableHeadCellProps[];
  rowCount: number;
  numSelected: number;
  onSort: any;
  onSelectAllRows: (checked: boolean) => void;
  handleFilterChange: any;
  setDateTimeFilter: any;
  selectRowPremission: boolean;
  sortFldList?: string[];
  isCampaining?: boolean;
  handleToggleSelectAll: any;
  selectAllFilter: any;
  count: number;
  defaultBedgecondition: boolean;
  selectedContact: any;
  setPage: any;
  resetFilterKey: number;
  dataFiltered: any[];
  notFound: boolean;
  updateColumns: TableHeadCellProps[];
  allColumns?: TableHeadCellProps[]; // All columns including hidden ones
  confirmDialog: any;
  makeCallDialog: any;
  selectedRowId: string | null;
  selectedPhoneNumber: any;
  deleteRecordWarning: string;
  handleDeleteRecord: (id: string) => void;
  setSelectedRowId: any;
  setSelectedPhoneNumber: any;
  registered: boolean;
  makeCall: any;
  handleCallUserDetails: any;
  handleFilterStatus: any;
  handlePhoneNumberClick: any;
  page: number;
  dense: boolean;
  onPageChange: any;
  onChangeDense: any;
  onRowsPerPageChange: any;
  theme: any;
  currentFilters: any;
  campaignState?: any;
  selectDefault?: boolean;
  selectableRowIds: string[];
  handleSmsQuickView?: (row: any) => void;
};

// ----------------------------------------------------------------------

export function TableCustomView({
  // Original props
  columns,
  tableToolbar,
  actionButton = '',
  actionButtonHandler = () => {},
  actionButtonIcon,
  secondaryActionButton,
  secondaryActionButtonHandler,
  secondaryActionButtonIcon,
  editHref,
  contactDetailHref,
  accountDetailHref,
  userDetailHref,
  teamsDetailHref,
  handleEditContact,
  tenantUserDetailsHref,
  tenantInvoiceDetailsHref,
  isToolbarVisible = true,
  isSelectRowPremission = true,
  isActionButton = [],
  actionhref = '',
  isShowCreateBtn = true,
  isSelectRecordDialog = false,
  setRecordInputHandler,
  isSearchFullWidth = false,
  handleDeleteRecords,
  handleMergeRecords,
  handleBulkDeleteClick,
  handleCustomiseDID,
  handleIVRConnection,
  handleToggleStatus,
  handleQuickEdit,
  loader = false,
  handleResendMail,
  handlePreview,
  playCallRecording = () => {},
  currentlyPlayingId = null,
  advanceFilterFields = [],
  isAdvanceFilter = false,
  refreshPage = undefined,
  refreshing = false,
  tabFilters = false,
  customTabs = [],
  isPrivacyPolicyUrls = null,
  handleLoginAsUser = () => {},
  handleOpenConversation = () => {},
  userRole = '',
  isDisableSync = false,
  isSupportReport = false,
  displayPagination = true,
  isSearchInput = true,
  isTenantInvoice = false,
  handleMarkAsRead = () => {},
  isSuperAdmin = false,
  currentTenantId = '',
  handleAddFriend = () => {},
  handlePayNowButton = () => {},
  invoiceLockedMessage = '',
  handleApproveLearning,
  handleRejectLearning,
  isApprovingLearning = false,
  isRejectingLearning = false,
  handleResolveKnowledgeGap,
  isResolvingKnowledgeGap = false,
  isRowInteractionLocked,
  rowInteractionLockedTooltip = '',
  getRowInteractionLockedTooltip,
  handleRefresh,
  onFileSelect = () => {},
  onExportContact = () => {},
  currentModule = '',
  isCampaignTamplate = false,
  isSelectAudience = false,
  customFields = [],
  disableSelectAll = false,
  handleSmsQuickView,

  // Flattened props from logic hook
  t,
  moduleName,
  isCreate,
  isView,
  isImport,
  isExport,
  isEdit,
  isDelete,
  table,
  filters,
  visibleColumns,
  setVisibleColumns,
  onResetPage,
  selectedfilters,
  dateTimeFiltre,
  handleRemoveAllFilter,
  resetPagination,
  order,
  orderBy,
  headCells,
  rowCount,
  numSelected,
  onSort,
  onSelectAllRows,
  handleFilterChange,
  setDateTimeFilter,
  selectRowPremission,
  sortFldList,
  isCampaining,
  handleToggleSelectAll,
  selectAllFilter,
  count,
  defaultBedgecondition,
  selectedContact,
  setPage,
  resetFilterKey,
  dataFiltered,
  notFound,
  updateColumns,
  confirmDialog,
  makeCallDialog,
  selectedRowId,
  selectedPhoneNumber,
  deleteRecordWarning,
  handleDeleteRecord,
  setSelectedRowId,
  setSelectedPhoneNumber,
  registered,
  makeCall,
  handleCallUserDetails,
  handleFilterStatus,
  handlePhoneNumberClick,
  page,
  dense,
  onPageChange,
  onChangeDense,
  onRowsPerPageChange,
  theme,
  currentFilters,
  allColumns, // All columns for the dropdown
  selectableRowIds,
}: TableCustomViewProps) {
  const myTableHeadElement = document.getElementsByClassName('myTableHead');

  const renderConfirmDialog = () => (
    <ConfirmDialog
      open={confirmDialog.value}
      onClose={() => {
        confirmDialog.onFalse();
        setSelectedRowId(null);
      }}
      title={t('DELETE')}
      content={
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('DELETE_RECORD_DESC')} <strong> {selectedRowId ? 1 : table.selected.length} </strong>
            {`${t(moduleName ?? 'UNKNOWN')}  `}
            {selectedRowId || table.selected.length === 1 ? t('RECORD') : t('RECORDS')} ?
          </Typography>
          {moduleName && ['CONTACT', 'CHANNEL'].includes(moduleName) && (
            <Typography variant="body2" color="text.secondary">
              {deleteRecordWarning}
            </Typography>
          )}
        </>
      }
      action={
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            const deleteRecords = selectedRowId ? [selectedRowId] : table.selected;
            handleDeleteRecords?.(deleteRecords);
            confirmDialog.onFalse();
            setSelectedRowId(null);
            table.setSelected([]);
          }}
        >
          {t('DELETE')}
        </Button>
      }
    />
  );

  const renderMakeCallDialog = () => (
    <ConfirmDialog
      open={makeCallDialog.value}
      onClose={() => {
        makeCallDialog.onFalse();
        setSelectedPhoneNumber(null);
      }}
      title={t('MAKE_CALL')}
      content={
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('DO_YOU_WANT_TO_CALL')} <strong>{selectedPhoneNumber?.phone}</strong>?
          </Typography>
          {!registered && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {t('PBX_NOT_REGISTERED_ERROR')}
            </Typography>
          )}
        </>
      }
      action={
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (selectedPhoneNumber) {
              makeCall(selectedPhoneNumber?.phone?.replace('+', ''));
              handleCallUserDetails(selectedPhoneNumber);
            }
            makeCallDialog.onFalse();
            setSelectedPhoneNumber(null);
          }}
          disabled={!registered}
        >
          {t('CALL')}
        </Button>
      }
    />
  );

  return (
    <>
      <Card sx={{ m: 1 }}>
        {tabFilters && (
          <Tabs
            value={currentFilters.status}
            onChange={handleFilterStatus}
            sx={{
              px: 2.5,
              boxShadow: `inset 0 -2px 0 0 ${varAlpha(theme.vars.palette.grey['500Channel'], 0.08)}`,
            }}
          >
            {customTabs.map((tab: any) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={t(tab.label)}
                iconPosition="end"
                icon={
                  <Label
                    variant={
                      ((tab.value === 'ALL' || tab.value === currentFilters.status) && 'filled') ||
                      'soft'
                    }
                    color={tab.color}
                  >
                    {tab.count}
                  </Label>
                }
              />
            ))}
          </Tabs>
        )}

        {tableToolbar && (
          <TableToolbar
            moduleName={moduleName}
            isCreate={['call', 'callreport', 'template']?.includes(moduleName) ? true : isCreate}
            isView={isView}
            isImport={isImport}
            isExport={isExport}
            filters={filters}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
            onResetPage={onResetPage}
            selectedfilters={selectedfilters}
            dateTimeFiltre={dateTimeFiltre}
            handleRemoveAllFilter={handleRemoveAllFilter}
            resetPagination={resetPagination}
            options={{ roles: [] }}
            actionButtonLabel={actionButton}
            actionButtonHandler={actionButtonHandler}
            actionButtonIcon={actionButtonIcon}
            secondaryActionButtonLabel={secondaryActionButton}
            secondaryActionButtonHandler={secondaryActionButtonHandler}
            secondaryActionButtonIcon={secondaryActionButtonIcon}
            isToolbarVisible={isToolbarVisible}
            isActionButton={isActionButton}
            actionhref={actionhref}
            isShowCreateBtn={isShowCreateBtn}
            isSearchFullWidth={isSearchFullWidth}
            onFileSelect={onFileSelect}
            onExportContact={onExportContact}
            currentModule={currentModule}
            advanceFilterFields={advanceFilterFields}
            setAdvanceFilter={table.setAdvanceFilter}
            advanceFilters={table.advanceFilter}
            isAdvanceFilter={isAdvanceFilter}
            refreshPage={refreshPage}
            refreshing={refreshing}
            isPrivacyPolicyUrls={isPrivacyPolicyUrls}
            isDisableSync={isDisableSync}
            isSearchInput={isSearchInput}
            columns={updateColumns}
            allColumns={allColumns} // Pass all columns for the dropdown
            customFields={customFields}
          />
        )}

        <Box sx={{ position: 'relative' }}>
          {!isSelectRecordDialog && !isCampaignTamplate && !isSelectAudience && (
            <TableSelectedAction
              dense={table.dense}
              numSelected={table.selected.length}
              rowCount={dataFiltered.length}
              onSelectAllRows={(checked) => {
                table.onSelectAllRows(checked, selectableRowIds);
              }}
              sx={{ height: `${myTableHeadElement[0]?.scrollHeight || 0}px` }}
              action={
                <>
                  {handleMergeRecords && table.selected.length >= 2 && (
                    <Tooltip
                      title={
                        table.selected.length > 2 ? t('ONLY_TWO_RECORDS_CAN_BE_MERGED') : t('MERGE')
                      }
                    >
                      <span>
                        <IconButton
                          color="primary"
                          onClick={() =>
                            handleMergeRecords(table.selected, () => table.setSelected([]))
                          }
                          disabled={table.selected.length > 2}
                        >
                          <Iconify icon="mdi:call-merge" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {isDelete && (
                    <Tooltip title={t('DELETE')}>
                      <IconButton
                        color="primary"
                        onClick={
                          handleBulkDeleteClick
                            ? () => handleBulkDeleteClick(table.selected, () => table.setSelected([]))
                            : confirmDialog.onTrue
                        }
                      >
                        <Iconify
                          icon="solar:trash-bin-trash-bold"
                          sx={{ color: 'var(--palette-error-main) !important' }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              }
            />
          )}

          <Scrollbar>
            <Table
              size={table.dense ? 'small' : 'medium'}
              sx={{
                width: '100%',
               
              }}
            >
              <TableHeadCustom
                order={order}
                orderBy={orderBy}
                headCells={headCells}
                rowCount={rowCount}
                numSelected={numSelected}
                onSort={onSort}
                onSelectAllRows={onSelectAllRows}
                handleFilterChange={handleFilterChange}
                setDateTimeFilter={setDateTimeFilter}
                selectRowPremission={selectRowPremission}
                sortFldList={sortFldList}
                isCampaignTamplate={isCampaignTamplate}
                isCampaining={isCampaining}
                handleToggleSelectAll={handleToggleSelectAll}
                selectAllFilter={selectAllFilter}
                count={count}
                defaultBedgecondition={defaultBedgecondition}
                selectedContact={selectedContact}
                dateTimeFiltre={dateTimeFiltre}
                setPage={setPage}
                resetFilterKey={resetFilterKey}
                moduleName={moduleName}
                isEdit={isEdit}
                isDelete={isDelete}
                disableSelectAll={disableSelectAll}
              />
              <TableBody>
                {loader ? (
                  Array.from({ length: moduleName === 'billing' ? 5 : 10 }).map((_, index) => (
                    <TableRow key={index}>
                      {columns.map((col, colIndex) => (
                        <TableCell key={colIndex}>
                          <Skeleton variant="text" width="100%" height={35} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <>
                    <TableRowCustom
                      t={t}
                      rows={dataFiltered}
                      table={table}
                      editHref={editHref}
                      contactDetailHref={contactDetailHref}
                      accountDetailHref={accountDetailHref}
                      teamsDetailHref={teamsDetailHref}
                      userDetailHref={userDetailHref}
                      onDeleteRow={() => {}}
                      headCells={updateColumns}
                      selectRowPremission={isSelectRowPremission}
                      handleEditContact={handleEditContact}
                      handleDeleteRecord={handleDeleteRecord}
                      handleCustomiseDID={handleCustomiseDID}
                      handleIVRConnection={handleIVRConnection}
                      isSelectRecordDialog={isSelectRecordDialog}
                      setRecordInputHandler={setRecordInputHandler}
                      handleResendMail={handleResendMail}
                      handlePreview={handlePreview}
                      isCampaignTamplate={isCampaignTamplate}
                      isEdit={isEdit}
                      isDelete={isDelete}
                      playCallRecording={playCallRecording}
                      currentlyPlayingId={currentlyPlayingId}
                      selectAllFilter={table.selectAllFilter}
                      moduleName={moduleName}
                      handleLoginAsUser={handleLoginAsUser}
                      userRole={userRole}
                      isSupportReport={isSupportReport}
                      handlePhoneNumberClick={handlePhoneNumberClick}
                      handleOpenConversation={handleOpenConversation}
                      isTenantInvoice={isTenantInvoice}
                      handleMarkAsRead={handleMarkAsRead}
                      tenantUserDetailsHref={tenantUserDetailsHref}
                      tenantInvoiceDetailsHref={tenantInvoiceDetailsHref}
                      isSuperAdmin={isSuperAdmin}
                      currentTenantId={currentTenantId}
                      handleAddFriend={handleAddFriend}
                      handlePayNowButton={handlePayNowButton}
                      invoiceLockedMessage={invoiceLockedMessage}
                      handleApproveLearning={handleApproveLearning}
                      handleRejectLearning={handleRejectLearning}
                      isApprovingLearning={isApprovingLearning}
                      isRejectingLearning={isRejectingLearning}
                      handleResolveKnowledgeGap={handleResolveKnowledgeGap}
                      isResolvingKnowledgeGap={isResolvingKnowledgeGap}
                      handleToggleStatus={handleToggleStatus}
                      handleQuickEdit={handleQuickEdit}
                      isRowInteractionLocked={isRowInteractionLocked}
                      rowInteractionLockedTooltip={rowInteractionLockedTooltip}
                      getRowInteractionLockedTooltip={getRowInteractionLockedTooltip}
                      handleRefresh={handleRefresh}
                      handleSmsQuickView={handleSmsQuickView}
                    />

                    <TableEmptyRows
                      height={table.dense ? 56 : 56 + 20}
                      emptyRows={emptyRows(table.page, table.rowsPerPage, dataFiltered.length)}
                    />

                    <TableNoData notFound={notFound} />
                  </>
                )}
              </TableBody>
            </Table>
          </Scrollbar>
        </Box>

        {displayPagination && (
          <TablePaginationCustom
            page={page}
            dense={dense}
            count={count}
            rowsPerPage={table.rowsPerPage}
            onPageChange={onPageChange}
            onChangeDense={onChangeDense}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        )}
      </Card>
      {renderConfirmDialog()}
      {renderMakeCallDialog()}
    </>
  );
}
